<template>
    <div class="third-party-console-container">
        <h4 class="paddingSmallBottom">Third Party Console</h4>
        <div v-if="info.active" class="header">
            <p>Third Party Script is currently running for auction {{ activeAuction?.id }}</p>

            <div class="auctionButtons">
                <button class="endBtn" @click="endThirdParty()">End Script</button>
            </div>
        </div>
        <div v-else-if="activeAuction?.id && auctionHasEnded === false" class="header">
            <p>Third Party Script is not running</p>
            <button v-if="thirdPartyViewerUrl" class="startBtn" @click="startThirdParty()">Start Third Party Script</button>
        </div>

        <ThirdPartyLogStream :activeAuction="activeAuction" :info="info" :thirdPartyCompany="thirdPartyCompany"></ThirdPartyLogStream>

        <!-- Generic Alert -->
        <AlertComponent v-if="showAlert" v-bind:title="alertTitle"
            v-bind:message="alertMessage" v-bind:secondaryAction=null @close="closeAlert()">
        </AlertComponent>
    </div>
</template>

<script>

import apiMixin from '@/mixins/apiMixin.js'
import AlertComponent from '@/components/Shared/AlertComponent.vue'
import ThirdPartyLogStream from './ThirdPartyLogStream.vue'

export default {
    name: 'ThirdPartyConsole',
    components: {
        AlertComponent,
        ThirdPartyLogStream,
    },
    mixins: [apiMixin],
    props: {
        activeAuction: {
            type: Object,
        },
        nextAuction: {
            type: Object,
        },
        thirdPartyCompany: {
            type: String,
            default: 'liveauctioneers'
        }
    },
    data() {
        return {
            info: {},
            // Alerts
            showAlert: false,
            alertTitle: '',
            alertMessage: '',
        }
    },
    default() {
        return {
            thirdPartyCompany: 'liveauctioneers',
        }
    },
    computed: {
        auctionHasEnded() {
            const endDate = new Date(this.activeAuction?.end_date);
            const now = new Date();
            return endDate < now;
        },
        thirdPartyViewerUrl: function () {
            if (!this.activeAuction) {
                return null;
            }
            switch(this.thirdPartyCompany){
                case 'liveauctioneers':
                    return this.activeAuction.live_auctioneers_url
                case 'invaluable':
                    return this.activeAuction.invaluable_url
            }
            return null;
        }
    },
    mounted() {
        this.subscribeToSSE();
        this.$root.$on('end-third-party', this.endThirdParty);
    },
    beforeDestroy() {
        this.unsubscribeFromSSE();
        this.$root.$off('end-third-party', this.endThirdParty);
    },
    methods: {

        /**
         * Close the alert
         */
        closeAlert() {
            this.showAlert = false
            if (this.shouldCloseModalAndNotExit === false) {
                this.exit()
            }
        },

        /**
         * Start the third party script
         */
        startThirdParty() {
            var cmp = this
            let apiPayload = {}
            apiPayload.apiEndpoint = 'active_auction'
            apiPayload.postData = {}
            apiPayload.postData.action = 'thirdparty:start'
            apiPayload.postData.auction_id = this.activeAuction?.id
            apiPayload.postData.company = this.thirdPartyCompany

            this.loadingTitle = 'Starting third party script'
            this.showLoading = true
            cmp.$store.dispatch('apiCall', apiPayload).then(response => {
                this.showLoading = false
                if (response.data.result !== 'success') {
                    this.alertTitle = 'Error'
                    this.alertMessage = response.data.message
                    this.showAlert = true
                } else {
                    this.alertTitle = 'Success'
                    this.alertMessage = 'Third party script started!'
                    this.showAlert = true
                }
            })
        },

        /**
         * End the third party script
         */
        endThirdParty(showAlerts = true) {
            var cmp = this
            let apiPayload = {}
            apiPayload.apiEndpoint = 'active_auction'
            apiPayload.postData = {}
            apiPayload.postData.action = 'thirdparty:end'
            apiPayload.postData.auction_id = this.activeAuction?.id
            apiPayload.postData.company = this.thirdPartyCompany

            if (showAlerts) {
                this.loadingTitle = 'Ending third party script'
                this.showLoading = true
            }

            cmp.$store.dispatch('apiCall', apiPayload).then(response => {
                this.showLoading = false
                if (response.data.result !== 'success') {
                    this.alertTitle = 'Error'
                    this.alertMessage = response.data.message
                    this.showAlert = true
                } else {
                    if (showAlerts) {
                        this.alertTitle = 'Success'
                        this.alertMessage = 'Third party script ended!'
                        this.showAlert = true
                    }
                }
            })
        },

        /**
         * Subscribe to the SSE
         */
        subscribeToSSE() {
            this.agentEventSource = new EventSource(`/api/active_auction/?action=thirdparty:status`, {
                withCredentials: true
            });
            this.agentEventSource.onopen = () => {
                console.debug('SSE connection opened');
            };
            this.agentEventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.info = data.data;
                    this.$root.$emit('third-party-info-updated', this.info);
                } catch (error) {
                    console.error('Error parsing JSON: ', error);
                }
            };
            this.agentEventSource.onerror = (error) => {
                console.error('SSE error: ', error);
            };
            window.addEventListener('beforeunload', this.unsubscribeFromSSE);
        },

        /**
         * Unsubscribe from the SSE
         */
        unsubscribeFromSSE() {
            if (this.eventSource) {
                this.eventSource.close();
            }
            window.removeEventListener('beforeunload', this.unsubscribeFromSSE);
        },
    },
}
</script>

<style scoped>
button {
    background-color: #007AFF;
    color: white;
    border: 1px solid #007AFF;
    border-radius: 4px;
}

.header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: auto;
    border-radius: 4px;
    padding: 1rem;
    align-items: center;
}
</style>